import { motion } from "framer-motion";
import { IMAGES } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  {
    num: "01",
    title: "The Oracle",
    body: "Umi is an oracle and spiritual consultant rooted in African American folk tradition. Her counsel is honest, unhurried, and grounded — no performance, no pretense. Just spirit, spoken plainly.",
    testId: "about-chapter-01",
  },
  {
    num: "02",
    title: "The Work",
    body: "Drawing from her relationship with the land, ancestral memory, and Hoodoo traditions of the American South — every session honors the lineage that carried this knowledge here.",
    testId: "about-chapter-02",
  },
  {
    num: "03",
    title: "The Community",
    body: "Culture. Candor. Community. This is a space built for real conversation about wellness, spirit, and the sacred work of becoming — Virginia-sown, Mississippi-grown, open to all who come correct.",
    testId: "about-chapter-03",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative overflow-hidden bg-[#131110] py-28 md:py-40">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-[1.15fr_1fr] lg:gap-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D49A36] md:text-xs"
          >
            About Umi
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-4xl font-medium leading-[1.02] tracking-tight text-[#F2EFE9] md:text-6xl"
          >
            A practice older than the <span className="italic text-[#D49A36]">road it traveled</span> to get here.
          </motion.h2>

          <div className="mt-16 space-y-14 md:mt-20">
            {CHAPTERS.map((chapter, i) => (
              <motion.article
                key={chapter.testId}
                data-testid={chapter.testId}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
                className="grid gap-4 border-t border-[#D49A36]/15 pt-8 md:grid-cols-[80px_1fr] md:gap-8"
              >
                <span className="font-display text-3xl italic text-[#8C382A] md:text-4xl">{chapter.num}</span>
                <div>
                  <h3 className="font-display text-2xl font-medium text-[#F2EFE9] md:text-3xl">{chapter.title}</h3>
                  <p className="mt-3 max-w-xl text-base leading-relaxed text-[#DCD8D3] md:text-lg">{chapter.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="relative self-start lg:sticky lg:top-28"
        >
          <div className="glow-flicker absolute -inset-10 rounded-full bg-[#D49A36] opacity-15 blur-[110px]" aria-hidden="true" />
          <figure className="relative">
            <div className="overflow-hidden rounded-t-[999px] border border-[#D49A36]/25 shadow-[0_0_60px_rgba(212,154,54,0.12)]">
              <motion.img
                data-testid="about-portrait-image"
                src={IMAGES.portrait}
                alt="Umi seated in a caramel chair before shelves of books and ancestral art"
                className="aspect-[3/4] w-full object-cover"
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: EASE }}
              />
            </div>
            <figcaption className="mt-5 text-center text-xs uppercase tracking-[0.25em] text-[#DCD8D3]/70">
              Umi — Oracle, Speaker, Mentor
            </figcaption>
          </figure>
        </motion.div>
      </div>
    </section>
  );
}
